import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Reveal from '../components/Reveal';

const sections = [
  {
    title: 'Introduction',
    body: (
      <p className="body-large text-white/75">
        Deft Experiences ("we", "our", or "us") is committed to protecting your privacy. This
        Privacy Policy explains how we collect, use, and safeguard your personal information when
        you use our services.
      </p>
    ),
  },
  {
    title: 'Information We Collect',
    body: (
      <>
        <p className="body-large text-white/75 mb-5">
          We collect information that you provide directly to us, including:
        </p>
        <ul className="space-y-2.5 body-large text-white/70">
          {[
            'Name, email address, and contact information',
            'Profile information including interests, goals, and professional background',
            'Event registration and attendance data',
            'Communications with us',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FFD1] mt-3 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    title: 'How We Use Your Information',
    body: (
      <>
        <p className="body-large text-white/75 mb-5">We use the information we collect to:</p>
        <ul className="space-y-2.5 body-large text-white/70">
          {[
            'Process your membership application and manage your account',
            'Send you event invitations and updates',
            'Improve our services and user experience',
            'Communicate with you about community activities',
            'Ensure the quality and curation of our community',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FFD1] mt-3 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    title: 'Information Sharing',
    body: (
      <p className="body-large text-white/75">
        We do not sell your personal information. We may share your information with service
        providers who assist in operating our platform, conducting events, or serving our
        community. All such parties are bound by confidentiality agreements.
      </p>
    ),
  },
  {
    title: 'Data Security',
    body: (
      <p className="body-large text-white/75">
        We implement appropriate technical and organizational measures to protect your personal
        information against unauthorized access, alteration, disclosure, or destruction.
      </p>
    ),
  },
  {
    title: 'Your Rights',
    body: (
      <>
        <p className="body-large text-white/75 mb-5">You have the right to:</p>
        <ul className="space-y-2.5 body-large text-white/70">
          {[
            'Access your personal information',
            'Request correction of inaccurate data',
            'Request deletion of your data',
            'Opt-out of marketing communications',
            'Withdraw consent for data processing',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FFD1] mt-3 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    title: 'Contact Us',
    body: (
      <p className="body-large text-white/75">
        If you have questions about this Privacy Policy or our data practices, please contact us at{' '}
        <a
          href="mailto:parvandh@deftexp.in"
          className="text-[#00FFD1] hover:underline"
          data-testid="privacy-contact-email"
        >
          parvandh@deftexp.in
        </a>
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="dark-container" data-testid="privacy-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-radial-teal opacity-40 pointer-events-none" />
        <div className="dark-content-container relative pt-16 pb-14 lg:pt-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel text-white/80 hover:text-[#00FFD1] hover:border-[#00FFD1]/40 transition-all text-sm font-medium mb-10"
            data-testid="privacy-back-link"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="max-w-4xl">
            <span className="eyebrow mb-5 block">Legal</span>
            <h1 className="display-huge text-white" data-testid="privacy-title">
              Privacy <span className="text-[#64C8FF]">Policy</span>
            </h1>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="relative">
        <div className="dark-content-container py-20 lg:py-32">
          <div className="max-w-3xl mx-auto space-y-14">
            {sections.map((s, i) => (
              <Reveal key={i} delay={i * 60} className={i === 0 ? '' : 'border-t border-white/8 pt-10'}>
                <h2 className="display-medium text-white mb-6">{s.title}</h2>
                {s.body}
              </Reveal>
            ))}
            <div className="pt-10 border-t border-white/8">
              <p className="body-small text-white/40">Last updated: January 2026</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
